import React, { useState } from 'react';
import { X, Heart, Bookmark, Pencil, Save, ArrowLeft } from 'lucide-react';
import { Quote } from '../types';
import { motion, AnimatePresence } from 'framer-motion';

interface QuoteModalProps {
  quote: Quote;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<Quote>) => Promise<{ success: boolean; error?: string }>;
}

export default function QuoteModal({ quote, onClose, onUpdate }: QuoteModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(quote);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggle = (field: 'isFavorite' | 'quotewall') => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));

    handleSaveToggle(field, !formData[field]);
  };

  const handleSaveToggle = async (field: 'isFavorite' | 'quotewall', value: boolean) => {
    try {
      const result = await onUpdate(quote.id, { [field]: value });
      if (!result.success && result.error) {
        setError(result.error);
        setFormData(prev => ({
          ...prev,
          [field]: !value
        }));
      }
    } catch (err) {
      setError('Failed to update quote');
      setFormData(prev => ({
        ...prev,
        [field]: !value
      }));
    }
  };

  const validateForm = () => {
    const errors: string[] = [];

    if (!formData.text.trim()) {
      errors.push('Quote text is required');
    } else if (formData.text.length > 500) {
      errors.push('Quote must be less than 500 characters');
    }

    if (!formData.author.trim()) {
      errors.push('Author name is required');
    }

    if (formData.age && (isNaN(Number(formData.age)) || Number(formData.age) < 0 || Number(formData.age) > 150)) {
      errors.push('Age must be between 0 and 150');
    }

    if (formData.year) {
      const yearNum = Number(formData.year);
      if (isNaN(yearNum) || yearNum < 1000 || yearNum > new Date().getFullYear()) {
        errors.push('Please enter a valid year');
      }
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const result = await onUpdate(quote.id, formData);
      if (result.success) {
        setIsEditing(false);
      } else {
        setError(result.error || 'Failed to update quote');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-praise max-w-2xl w-full max-h-[90vh] overflow-hidden"
      >
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isEditing ? (
                <button
                  type="button"
                  onClick={() => {
                    setFormData(quote);
                    setIsEditing(false);
                    setError(null);
                  }}
                  className="btn-secondary p-2"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : (
                <h2 className="text-xl font-display font-semibold text-gray-900">
                  Quote Details
                </h2>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary p-2"
                >
                  <Pencil className="h-5 w-5" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary p-2"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"
                >
                  {error}
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="ml-auto text-red-700 hover:text-red-900"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </motion.div>
              )}

              <div className="space-y-6">
                {/* Quote Text */}
                <div>
                  {isEditing ? (
                    <div className="space-y-2">
                      <label htmlFor="text" className="block text-sm font-medium text-gray-700">
                        Quote Text
                      </label>
                      <textarea
                        id="text"
                        name="text"
                        value={formData.text}
                        onChange={handleChange}
                        rows={3}
                        className="input"
                      />
                      <p className="text-sm text-gray-500">
                        {500 - formData.text.length} characters remaining
                      </p>
                    </div>
                  ) : (
                    <div className="card p-6">
                      <p className="text-xl text-gray-900 italic">"{formData.text}"</p>
                      <p className="mt-4 text-lg font-medium text-gray-700">— {formData.author}</p>
                    </div>
                  )}
                </div>

                {/* Author */}
                {isEditing && (
                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                      Author
                    </label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      className="mt-1 input"
                    />
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Age</h3>
                    {isEditing ? (
                      <input
                        type="number"
                        name="age"
                        value={formData.age || ''}
                        onChange={handleChange}
                        min="0"
                        max="150"
                        className="input"
                      />
                    ) : (
                      <p className="text-gray-900">{formData.age || '—'}</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Year</h3>
                    {isEditing ? (
                      <input
                        type="number"
                        name="year"
                        value={formData.year || ''}
                        onChange={handleChange}
                        min="1000"
                        max={new Date().getFullYear()}
                        className="input"
                      />
                    ) : (
                      <p className="text-gray-900">{formData.year || '—'}</p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Location</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        name="location"
                        value={formData.location || ''}
                        onChange={handleChange}
                        className="input"
                      />
                    ) : (
                      <p className="text-gray-900">{formData.location || '—'}</p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Context</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        name="context"
                        value={formData.context || ''}
                        onChange={handleChange}
                        className="input"
                      />
                    ) : (
                      <p className="text-gray-900">{formData.context || '—'}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={() => handleToggle('isFavorite')}
                    className={`btn flex-1 ${
                      formData.isFavorite
                        ? 'bg-red-50 text-red-700 hover:bg-red-100'
                        : 'btn-secondary'
                    }`}
                  >
                    <Heart className={`h-5 w-5 mr-2 ${formData.isFavorite ? 'fill-red-500' : ''}`} />
                    {formData.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleToggle('quotewall')}
                    className={`btn flex-1 ${
                      formData.quotewall
                        ? 'bg-brand-50 text-brand-700 hover:bg-brand-100'
                        : 'btn-secondary'
                    }`}
                  >
                    <Bookmark className={`h-5 w-5 mr-2 ${formData.quotewall ? 'fill-brand-500' : ''}`} />
                    {formData.quotewall ? 'Remove from Quotewall' : 'Add to Quotewall'}
                  </button>
                </div>
              </div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          {isEditing && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setFormData(quote);
                  setIsEditing(false);
                  setError(null);
                }}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </form>
      </motion.div>
    </div>
  );
}